// FG Transfers + Production-In. Phase 1D-δ-1.
// Single file: list (with Incoming/Outgoing badge) + create dialog + detail dialog + production-in dialog.
//
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): mode SERVER penuh, keadaan di alamat halaman,
// saringan bawaan "Belum selesai" (draf + dikirim) + gudang sendiri sebagai ASAL ATAU TUJUAN (Admin Gudang —
// transfer masuk tidak boleh tersembunyi); dialog MENAMBAH `?create`/`?id`/`?production_in` ke alamat.
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { ArrowLeftRight, ArrowUpRight, ArrowDownLeft, Eye, Plus, RefreshCw, X, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../../lib/format";
import { TransferStatusBadge } from "../../components/etams/StatusBadge";
import {
  listFGTransfers, getFGTransfer, createFGTransfer,
  dispatchFGTransfer, receiveFGTransfer, cancelFGTransfer,
  productionIn, apiErrorMessage, fgNegativeStockMessage,
  isProductionInDuplicate,
  listFGBalance,
} from "../../lib/inventoryApi";
import { ambilSemua } from "../../lib/ambilSemua";
import Hint from "../../components/etams/Hint";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariWo, labelWo } from "../../lib/sumberPilihan";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const LABEL_SARING = { BELUM_SELESAI: "Belum selesai", DRAFT: "Draf", DISPATCHED: "Dikirim", RECEIVED: "Diterima", CANCELLED: "Dibatalkan" };
//: Cermin `URUT_SAH_FG_TRANSFER` di backend/services/finished_goods_service.py.
const URUT_SAH = ["created_at", "transfer_number", "status"];

/** Tambah/hapus kunci dialog di alamat TANPA membuang keadaan daftar (h, q, saringan). */
function ubahParamDialog(setSp, ubah) {
  setSp((prev) => {
    const n = new URLSearchParams(prev);
    ["create", "id", "production_in"].forEach((k) => n.delete(k));
    Object.entries(ubah || {}).forEach(([k, v]) => n.set(k, v));
    return n;
  }, { replace: true });
}

const LABEL_PRODUK = { ATASAN: "Atasan", CELANA: "Celana" };

// KONTRAS-01 Batch 9 — peta `STATUS_BADGE` lokal yang dulu di sini (4 status
// dengan `cls:` warna keras) pindah ke `INV_TRANSFER_STATUS_TONE` di
// `components/etams/StatusBadge.jsx`. Label tidak berubah sepotong pun;
// `CANCELLED` bergeser dari MERAH ke netral — alasannya di berkas itu.
//
// `directionBadge()`: ARAH (masuk/keluar/lintas) bukan STATUS. Owner 27 Sep 2026 (VONIS-27SEP-JAWABAN-10 butir 10,
// menutup `J3-ARAH-01`): arah tetap abu-abu dan dibedakan IKON PANAH + label; warna hanya untuk status.


export default function Transfers() {
  const { profile, roleCode } = useAuth();
  const [sp, setSp] = useSearchParams();
  const selectedId = sp.get("id");
  const showCreate = sp.get("create") === "1";
  const showProdIn = sp.get("production_in") === "1";

  const canCreate = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"].includes(roleCode);
  const canProdIn = ["OWNER", "ADMIN_PRODUKSI", "ADMIN_GUDANG"].includes(roleCode);

  const gudangSaya = (roleCode === "ADMIN_GUDANG" && profile?.store_id) || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // DUA daftar cabang, dan bedanya disengaja (P-1 Tahap 2).
  //
  //   `stores`     — dijepit wilayah. Untuk LABEL dan untuk dropdown ASAL.
  //   `semuaStores`— seluruh cabang. HANYA untuk dropdown TUJUAN.
  //
  // ⛔ Kenapa ASAL dijepit: `create_transfer` menolak asal di luar wilayah
  //    pemanggil dengan 403 (penjepit Tahap 1). Dropdown yang menawarkan
  //    cabang lain sebagai ASAL menawarkan pilihan yang PASTI ditolak —
  //    itu bug yang sudah ada, bukan pagar baru.
  //
  // ⚠ Kenapa TUJUAN tidak: barang memang berpindah antar cabang, dan itu
  //   justru gunanya transfer. `create_transfer` sengaja nol menjepit sisi
  //   tujuan.
  const [stores, setStores] = useState([]);
  const [semuaStores, setSemuaStores] = useState([]);

  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || [])).catch((e) => { setStores([]); toast.error(apiErrorMessage(e, "Daftar gudang gagal dimuat — muat ulang halaman.")); });
    api.get("/stores", { params: { untuk: "tujuan-transfer" } })
      .then(r => setSemuaStores(r.data || []))
      .catch((e) => { setSemuaStores([]); toast.error(apiErrorMessage(e, "Daftar gudang gagal dimuat — muat ulang halaman.")); });
  }, []);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: "BELUM_SELESAI", tampil: (v) => LABEL_SARING[v] || v },
      gudang: { label: "Gudang (asal atau tujuan)", param: "store_id", bawaan: gudangSaya,
                tampil: (v) => semuaStores.find(x => x.id === v)?.name || stores.find(x => x.id === v)?.name || "…" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listFGTransfers(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listFGTransfers(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat transfer."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);
  const tutupDialog = useCallback((r) => { ubahParamDialog(setSp, {}); if (r) load(); }, [setSp, load]);

  const directionBadge = (t) => {
    const isOwner = roleCode === "OWNER";
    const myStore = profile?.store_id;
    const isSrc = myStore && t.source_store_id === myStore;
    const isDst = myStore && t.dest_store_id === myStore;
    // ⛔ RONA-nya SENGAJA DIPERTAHANKAN, yang ditambah cuma varian gelap.
    //    `J3-ARAH-01` mencatat warna ARAH (masuk/keluar/lintas) BUKAN warna
    //    status, dan palet A2 nol muat untuknya — memilih palet baru di sini
    //    berarti agent memutuskan kosakata warna yang Owner belum putuskan
    //    (pagar Q68). Jadi yang diperbaiki HANYA cacatnya: di mode gelap,
    //    chip terang-di-atas-gelap nol terbaca.
    // ⚠ LITERAL, bukan dirakit string. Tailwind memindai kelas sebagai TEKS
    //   di kode; `bg-${h}-100` nol pernah ikut ter-generate, dan badge-nya
    //   kehilangan SELURUH warna sementara build tetap hijau — kelas cacat
    //   yang sama dengan `roles={undefined}` (AKSES-03).
    // J3-ARAH-01 DIPUTUS Owner 27 Sep 2026 (VONIS-27SEP-JAWABAN-10 butir 10, K201): warna arah transfer tetap
    //   ABU-ABU dan dibedakan dengan IKON PANAH; warna hanya untuk status (K73/K81, K200 butir 8).
    const NETRAL = "bg-neutral-soft text-neutral-soft-foreground";
    if (isOwner && !isSrc && !isDst) return { label: "Lintas Wilayah", cls: NETRAL, Ikon: ArrowLeftRight };
    if (isSrc && isDst) return { label: "Dua Arah", cls: NETRAL, Ikon: ArrowLeftRight };
    if (isSrc) return { label: "Keluar", cls: NETRAL, Ikon: ArrowUpRight };
    if (isDst) return { label: "Masuk", cls: NETRAL, Ikon: ArrowDownLeft };
    // "Lihat" netral — tidak punya arah.
    return { label: "Lihat", cls: "bg-muted text-muted-foreground", Ikon: Eye };
  };

  // ⚠ Nama cabang dicari di DIREKTORI dulu, baru daftar terjepit.
  //    Transfer MASUK dari cabang lain memang boleh terlihat (aturan
  //    `source ATAU dest`), jadi `source_store_id`-nya sering berada DI LUAR
  //    wilayah pemanggil. Mencarinya cuma di daftar terjepit membuat nama
  //    cabang jatuh jadi potongan uuid — pagar yang benar, terbaca sebagai
  //    data rusak.
  const storeName = (id) => (
    semuaStores.find(s => s.id === id)?.name
    || stores.find(s => s.id === id)?.name
    || "—"
  );

  return (
    <div className="space-y-6" data-testid="fg-transfer-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ArrowLeftRight className="h-6 w-6" /> Transfer Antar Cabang
            <Hint>Kirim barang jadi antar cabang: Draf → Dikirim → Diterima. Transfer yang sudah dikirim masih bisa dibatalkan, dan stok di cabang asal otomatis dikembalikan.</Hint>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          {canProdIn && (
            <Button size="sm" variant="outline" onClick={() => ubahParamDialog(setSp, { production_in: "1" })} data-testid="prod-in-btn">
              <PackagePlus className="h-4 w-4 mr-1" /> Masuk dari Produksi
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => ubahParamDialog(setSp, { create: "1" })} data-testid="fg-transfer-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Transfer Baru
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor transfer" testId="fg-transfer-palang"
                    ekspor={{ nama: "transfer_barang_jadi", ambilHalaman, kolom: [
                      { kunci: "transfer_number", judul: "No. Transfer" },
                      { kunci: "dari", judul: "Dari", ambil: (t) => storeName(t.source_store_id) },
                      { kunci: "ke", judul: "Ke", ambil: (t) => storeName(t.dest_store_id) },
                      { kunci: "status", judul: "Status", ambil: (t) => LABEL_SARING[t.status] || t.status },
                      { kunci: "created_at", judul: "Tanggal", ambil: (t) => formatDateTime(t.created_at) },
                    ] }}>
        <select value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="fg-transfer-status-filter" aria-label="Saring status">
          <option value="">Semua Status</option>
          {Object.entries(LABEL_SARING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={daftar.nilaiSaringan.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="fg-transfer-store-filter" aria-label="Saring gudang">
          <option value="">Semua Gudang</option>
          {(semuaStores.length ? semuaStores : stores).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </PalangDaftar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="transfer_number" className="px-4 py-3 font-medium">No. Transfer</ThUrut>
                <th className="px-4 py-3 font-medium">Arah</th>
                <th className="px-4 py-3 font-medium">Dari</th>
                <th className="px-4 py-3 font-medium">Ke</th>
                <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="7" className="p-12 text-center text-muted-foreground text-sm" data-testid="fg-transfer-kosong">
                  {daftar.cari.trim() || daftar.saringanAktif.length
                    ? "Tidak ada transfer yang cocok dengan pencarian atau saringan ini."
                    : "Belum ada transfer."}
                </td></tr>
              )}
              {daftar.tampil.map((t) => {
                const dir = directionBadge(t);
                return (
                  <tr key={t.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-mono text-xs">{t.transfer_number}</td>
                    <td className="px-4 py-3"><Badge className={`${dir.cls} gap-1`} data-testid="arah-transfer"><dir.Ikon className="h-3.5 w-3.5" aria-hidden="true" />{dir.label}</Badge></td>
                    <td className="px-4 py-3">{storeName(t.source_store_id)}</td>
                    <td className="px-4 py-3">{storeName(t.dest_store_id)}</td>
                    <td className="px-4 py-3"><TransferStatusBadge value={t.status} /></td>
                    <td className="px-4 py-3">{formatDateTime(t.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => ubahParamDialog(setSp, { id: t.id })}
                              data-testid={`fg-transfer-detail-${t.id}`}>Detail</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="fg-transfer-paginasi" />
      </Card>

      {showCreate && <TransferCreateDialog stores={stores} semuaStores={semuaStores}
                                           onClose={tutupDialog} />}
      {/* ⛔ ProductionInDialog SENGAJA cuma menerima daftar terjepit: tujuan
          production-in WAJIB gudang di wilayah sendiri. Barang fisik nol bisa
          berpindah cabang tanpa melewati transfer (keputusan Owner 6 Sep),
          dan `production_in` menegakkannya dengan 403. */}
      {showProdIn && <ProductionInDialog stores={stores} onClose={tutupDialog} />}
      {selectedId && (
        <TransferDetailDialog
          id={selectedId} stores={stores} semuaStores={semuaStores}
          profile={profile} roleCode={roleCode}
          onClose={tutupDialog}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
function TransferCreateDialog({ stores, semuaStores, onClose }) {
  const [srcId, setSrcId] = useState("");
  const [dstId, setDstId] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([{ wo_id: "", product_type: "ATASAN", qty: 1 }]);
  const [saving, setSaving] = useState(false);
  // Inventaris C20: WO dipilih dari saldo barang jadi gudang asal — dulu UUID diketik tangan.
  const [saldoAsal, setSaldoAsal] = useState([]);
  useEffect(() => {
    if (!srcId) { setSaldoAsal([]); return; }
    (async () => {
      try {
        const semua = await ambilSemua(listFGBalance, { store_id: srcId });
        setSaldoAsal(semua.filter((x) => Number(x.qty_available) > 0));
      } catch (e) { toast.error(apiErrorMessage(e, "Saldo barang jadi gudang asal gagal dimuat.")); setSaldoAsal([]); }
    })();
  }, [srcId]);
  const woAsal = [...new Map(saldoAsal.map((x) => [x.wo_id, x.wo?.wo_number || x.wo_id])).entries()];

  const setRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const submit = async () => {
    if (!srcId || !dstId) { toast.error("Pilih gudang asal dan tujuan."); return; }
    if (srcId === dstId) { toast.error("Gudang asal dan tujuan tidak boleh sama."); return; }
    for (const r of rows) {
      if (!r.wo_id || !(r.qty > 0)) { toast.error("Setiap baris butuh WO dan jumlah lebih dari 0."); return; }
    }
    setSaving(true);
    try {
      await createFGTransfer({ source_store_id: srcId, dest_store_id: dstId, notes, items: rows });
      toast.success("Transfer dibuat sebagai draf.");
      onClose(true);
    } catch (e) {
      toast.error(fgNegativeStockMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-3xl" data-testid="fg-transfer-create-dialog">
        <DialogHeader><DialogTitle>Transfer Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">Dari Gudang *</span>
              <select value={srcId} onChange={(e) => setSrcId(e.target.value)}
                      className="w-full h-9 rounded border px-2 text-sm" data-testid="fg-transfer-src">
                <option value="">— Pilih —</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm">Ke Gudang *</span>
              {/* ⛔ SATU-SATUNYA dropdown yang memakai DIREKTORI PENUH.
                  Barang memang berpindah antar cabang — itu gunanya
                  transfer, dan `create_transfer` sengaja nol menjepit sisi
                  tujuan. Dropdown "Dari Cabang" di sebelah SENGAJA memakai
                  daftar terjepit: asal di luar wilayah pasti ditolak 403. */}
              <select value={dstId} onChange={(e) => setDstId(e.target.value)}
                      className="w-full h-9 rounded border px-2 text-sm" data-testid="fg-transfer-dst">
                <option value="">— Pilih —</option>
                {semuaStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Barang</span>
              <Button variant="outline" size="sm" onClick={() => setRows([...rows, { wo_id: "", product_type: "ATASAN", qty: 1 }])}>
                <Plus className="h-3 w-3 mr-1" /> Tambah
              </Button>
            </div>
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2 items-end">
                <label className="flex-1">
                  <span className="text-xs text-muted-foreground">WO (saldo di gudang asal)</span>
                  <select className="h-9 w-full rounded border px-2 text-sm" value={r.wo_id}
                          onChange={(e) => setRow(i, { wo_id: e.target.value })} data-testid={`transfer-wo-${i}`}>
                    <option value="">{srcId ? "— pilih WO —" : "pilih gudang asal dulu"}</option>
                    {woAsal.map(([id, no]) => <option key={id} value={id}>{no}</option>)}
                  </select>
                </label>
                <label className="w-32">
                  <span className="text-xs text-muted-foreground">Produk</span>
                  <select value={r.product_type} onChange={(e) => setRow(i, { product_type: e.target.value })}
                          className="w-full h-9 rounded border px-2 text-sm">
                    <option value="ATASAN">Atasan</option>
                    <option value="CELANA">Celana</option>
                  </select>
                </label>
                <label className="w-24">
                  <span className="text-xs text-muted-foreground">Jumlah</span>
                  <Input type="number" step="0.001" value={r.qty}
                         onChange={(e) => setRow(i, { qty: Number(e.target.value) })} />
                </label>
                {rows.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => setRows(rows.filter((_, x) => x !== i))}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <label className="block">
            <span className="text-sm">Catatan</span>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="fg-transfer-submit">
            {saving ? "Menyimpan..." : "Buat Draf"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDetailDialog({ id, stores, semuaStores, profile, roleCode, onClose }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await getFGTransfer(id);
      setT(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat transfer."));
      onClose(false);
    }
  }, [id, onClose]);

  useEffect(() => { load(); }, [load]);

  // Nomor WO untuk tampilan (bukan potongan ID): dibaca dari saldo barang jadi gudang asal.
  const [nomorWo, setNomorWo] = useState({});
  useEffect(() => {
    if (!t?.source_store_id) return;
    (async () => {
      try {
        const semua = await ambilSemua(listFGBalance, { store_id: t.source_store_id });
        setNomorWo(Object.fromEntries(semua.filter((x) => x.wo?.wo_number).map((x) => [x.wo_id, x.wo.wo_number])));
      } catch (e) { toast.error(apiErrorMessage(e, "Nomor WO gagal dimuat.")); }
    })();
  }, [t?.source_store_id]);

  const doAction = async (fn, msg) => {
    setBusy(true);
    try {
      await fn(id);
      toast.success(msg);
      onClose(true);
    } catch (e) {
      toast.error(fgNegativeStockMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!cancelReason.trim() || cancelReason.trim().length < 5) {
      toast.error("Alasan batal wajib minimal 5 karakter."); return;
    }
    setBusy(true);
    try {
      await cancelFGTransfer(id, { reason: cancelReason.trim() });
      toast.success(t.status === "DISPATCHED"
        ? "Transfer dibatalkan; stok dikembalikan ke gudang asal."
        : "Draf transfer dibatalkan.");
      onClose(true);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!t) {
    return <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent><div className="p-8 text-center text-muted-foreground">Memuat...</div></DialogContent>
    </Dialog>;
  }

  // Sama seperti di daftar: transfer masuk berasal dari cabang di luar
  // wilayah pemanggil, jadi namanya dicari di direktori dulu.
  const storeName = (sid) => (
    semuaStores.find(s => s.id === sid)?.name
    || stores.find(s => s.id === sid)?.name
    || "—"
  );
  const isOwner = roleCode === "OWNER";
  const isSrcAG = roleCode === "ADMIN_GUDANG" && profile?.store_id === t.source_store_id;
  const isDstAG = roleCode === "ADMIN_GUDANG" && profile?.store_id === t.dest_store_id;
  const canDispatch = (isOwner || isSrcAG) && t.status === "DRAFT";
  const canReceive = (isOwner || isDstAG) && t.status === "DISPATCHED";
  const canCancelTransfer = (isOwner || isSrcAG
    || (roleCode === "KEPALA_CABANG" && profile?.region_id === t.source_region_id))
    && ["DRAFT", "DISPATCHED"].includes(t.status);

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl" data-testid="fg-transfer-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t.transfer_number} <TransferStatusBadge value={t.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Dari:</span> {storeName(t.source_store_id)}</div>
            <div><span className="text-muted-foreground">Ke:</span> {storeName(t.dest_store_id)}</div>
            <div><span className="text-muted-foreground">Dibuat:</span> {formatDateTime(t.created_at)}</div>
            <div><span className="text-muted-foreground">Dikirim:</span> {formatDateTime(t.dispatched_at)}</div>
            <div><span className="text-muted-foreground">Diterima:</span> {formatDateTime(t.received_at)}</div>
            <div><span className="text-muted-foreground">Dibatalkan:</span> {formatDateTime(t.cancelled_at)}</div>
          </div>
          {t.cancel_reason && (
            <div className="text-sm bg-muted/50 border border-border rounded p-2">
              <span className="font-medium">Alasan Batal:</span> {t.cancel_reason}
            </div>
          )}
          <div>
            <div className="text-sm font-medium mb-2">Barang ({t.items?.length || 0})</div>
            <table className="w-full text-sm border">
              <thead className="bg-muted/50 border-b">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">WO</th>
                  <th className="px-3 py-2 font-medium">Produk</th>
                  <th className="px-3 py-2 font-medium text-right">Jumlah</th>
                  <th className="px-3 py-2 font-medium">Stok keluar</th>
                  <th className="px-3 py-2 font-medium">Stok masuk</th>
                </tr>
              </thead>
              <tbody>
                {(t.items || []).map(it => (
                  <tr key={it.id} className="border-b">
                    <td className="px-3 py-2 font-mono text-xs">{nomorWo[it.wo_id] || "—"}</td>
                    <td className="px-3 py-2">{LABEL_PRODUK[it.product_type] || "—"}</td>
                    <td className="px-3 py-2 text-right">{it.qty}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{it.movement_out_id ? "✓" : "-"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{it.movement_in_id ? "✓" : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onClose(false)}>Tutup</Button>
          {canDispatch && (
            <Button onClick={() => doAction(dispatchFGTransfer, "Transfer dikirim; stok gudang asal berkurang.")}
                    disabled={busy} data-testid="fg-transfer-dispatch-btn">Kirim</Button>
          )}
          {canReceive && (
            <Button onClick={() => doAction(receiveFGTransfer, "Transfer diterima; stok gudang tujuan bertambah.")}
                    disabled={busy} data-testid="fg-transfer-receive-btn">
              Terima
            </Button>
          )}
          {canCancelTransfer && !showCancel && (
            <Button variant="destructive" onClick={() => setShowCancel(true)} data-testid="fg-transfer-cancel-open">
              Batalkan
            </Button>
          )}
          {showCancel && (
            <div className="w-full space-y-2 pt-2 border-t">
              {t.status === "DISPATCHED" && (
                <div className="text-xs bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-2">
                  ⚠️ Membatalkan transfer yang sudah dikirim akan mengembalikan stok ke gudang asal.
                </div>
              )}
              <Textarea placeholder="Alasan pembatalan (min 5 karakter)..."
                        value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                        rows={2} data-testid="fg-transfer-cancel-reason" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCancel(false)}>Kembali</Button>
                <Button variant="destructive" size="sm" onClick={doCancel} disabled={busy}
                        data-testid="fg-transfer-cancel-confirm">Konfirmasi Batal</Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
function ProductionInDialog({ stores, onClose }) {
  const [woId, setWoId] = useState("");
  const [wo, setWo] = useState(null);
  const [storeId, setStoreId] = useState("");
  // Inventaris C20: pilih dari WO berstatus COMPLETED — dulu UUID diketik tangan.
  // ⛔ 27 Sep 2026 (bug paginasi #1): dulu SELURUH WO selesai dipindai dengan ukuran
  //    halaman 200 ke `/wo` yang saat itu menolak di atas 100 → 422, daftar kosong,
  //    dan WO selesai terus bertambah tanpa batas. Sekarang dicari di server.
  const cariWoSelesai = useCallback((kata) => cariWo(kata, { status: "COMPLETED" }), []);
  const [rows, setRows] = useState([{ product_type: "ATASAN", qty: 1 }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const setRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const submit = async () => {
    if (!woId || !storeId) { toast.error("WO dan gudang tujuan wajib dipilih."); return; }
    for (const r of rows) {
      if (!(r.qty > 0)) { toast.error("Jumlah tiap baris harus lebih dari 0."); return; }
    }
    setSaving(true);
    try {
      await productionIn({ wo_id: woId, store_id: storeId, items: rows, notes });
      toast.success("Barang masuk dari produksi tercatat; saldo barang jadi diperbarui.");
      onClose(true);
    } catch (e) {
      if (isProductionInDuplicate(e)) {
        toast.error("Barang masuk dari produksi untuk WO ini sudah pernah dicatat.");
      } else {
        toast.error(apiErrorMessage(e, "Gagal mencatat barang masuk dari produksi."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-xl" data-testid="prod-in-dialog">
        <DialogHeader><DialogTitle>Barang Masuk dari WO yang Selesai</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm">WO yang selesai *</span>
            <PemilihCari
              value={wo}
              onChange={(w) => { setWo(w); setWoId(w?.id || ""); }}
              cari={cariWoSelesai}
              label={labelWo}
              keterangan={(w) => w.store_name || ""}
              tampilAwal
              placeholder="— pilih WO selesai —"
              placeholderCari="Ketik nomor WO…"
              testId="prod-in-wo"
            />
          </label>
          <label className="block">
            <span className="text-sm">Gudang Tujuan *</span>
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)}
                    className="w-full h-9 rounded border px-2 text-sm" data-testid="prod-in-store">
              <option value="">— Pilih —</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Barang</span>
              <Button variant="outline" size="sm"
                      onClick={() => setRows([...rows, { product_type: "CELANA", qty: 1 }])}>
                <Plus className="h-3 w-3 mr-1" /> Tambah
              </Button>
            </div>
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2 items-end">
                <label className="flex-1">
                  <span className="text-xs text-muted-foreground">Produk</span>
                  <select value={r.product_type} onChange={(e) => setRow(i, { product_type: e.target.value })}
                          className="w-full h-9 rounded border px-2 text-sm">
                    <option value="ATASAN">Atasan</option>
                    <option value="CELANA">Celana</option>
                  </select>
                </label>
                <label className="w-32">
                  <span className="text-xs text-muted-foreground">Jumlah</span>
                  <Input type="number" step="0.001" value={r.qty}
                         onChange={(e) => setRow(i, { qty: Number(e.target.value) })} />
                </label>
                {rows.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => setRows(rows.filter((_, x) => x !== i))}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <label className="block">
            <span className="text-sm">Catatan</span>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="prod-in-submit">
            {saving ? "Menyimpan..." : "Catat Barang Masuk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
